// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.SubReportLink.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import {
  placeholder,
  toString,
  flow,
  map,
  mapValues,
  isNil,
  toUpper,
  find,
  get,
  trim,
  uniq,
  pick,
  isNull,
  isEqual,
  isEmpty,
  uniqWith,
  forEach,
  filter,
  head,
  isUndefined,
  assign,
  unset,
  set,
  omit,
  omitBy,
  getOr,
  toNumber,
  compact,
  concat
} from 'lodash/fp';
import { toJS } from 'mobx';
import fetcher from 'fetchers/Izenda.ReportDesignerDataFetcher';
import { COLUMN_NAME_PREFIX, FILTER_URL } from 'logic/data/Izenda.ReportDesignerConstants';
import { SUB_REPORT } from 'logic/data/Izenda.PropertiesBuilderConstants';
import { DEFAUFT_VALUE } from 'logic/data/Izenda.FilterDetailsConfigConstants';

const { ERROR_MESSAGE } = SUB_REPORT;

/**
 * Builder filter data common
 * @param {Array} records
 * @param {Array} subReportMappingFields
 */
function buildFilterDataCommon(records: Array<any>, subReportMappingFields: Array<any>, { previewDataFieldsMapping, fieldCollection }) {
  const result = {
    success: true
  };

  let filterData = flow(
    toJS,
    map(
      mapValues(
        value =>
          isNull(value) || isEqual('null', value)
            ? DEFAUFT_VALUE.NULL
            : isEqual('', value)
              ? DEFAUFT_VALUE.BLANK
              : toString(value)
      )
    )
  )(records);

  const formatFieldName = flow(
    trim,
    toUpper
  );
  const listFieldDontHaveMapping = [];

  let filters = subReportMappingFields.map((mappingField, index) => {
    const mappingFieldName = formatFieldName(mappingField.fieldNameAlias);

    const selectedFieldMapping = find(
      fieldMapping => formatFieldName(fieldMapping.fieldNameAlias) === mappingFieldName,
      previewDataFieldsMapping
    );
    const selectedElement = find(
      fieldElement => formatFieldName(fieldElement.name) === mappingFieldName,
      fieldCollection
    );

    const functionId = get('properties.dataFormattings.function', selectedElement);
    const formatData = get('properties.dataFormattings.format.formatId', selectedElement);

    if (!selectedFieldMapping || !selectedElement) {
      // dont find field in current report part, need to reconfig sub report seeting
      result.success = false;
      listFieldDontHaveMapping.push(mappingField.fieldNameAlias);
    }

    return {
      mappingField,
      mappingFieldIndex: index,
      dataFormat: formatData || undefined,
      function: functionId || undefined,
      columnName: get('columnName', selectedFieldMapping),
      dataType: get('field.dataFieldType', selectedElement)
    };
  });

  if (!result.success) {
    // dont find field in current report part, need to reconfig sub report seeting
    result.errors = String.format(ERROR_MESSAGE.CANT_RESOLVE_FIELD, listFieldDontHaveMapping.join(', '));
    return result;
  }
  const listColumnName = uniq(map('columnName', filters));
  const pickColumnName = pick(listColumnName);

  filterData = uniqWith((arrVal, othVal) => isEqual(pickColumnName(arrVal), pickColumnName(othVal)), filterData);

  if (filterData.length != 1) {
    // there are multiple data so can't handle field mapping
    result.success = false;
    let listFieldHaveMultipleValue = [];

    forEach(columnName => {
      if (uniq(map(columnName, filterData)).length !== 1) {
        listFieldHaveMultipleValue.push(
          flow(
            filter({ columnName: columnName }),
            map('mappingField.fieldNameAlias'),
            uniq
          )(filters)
        );
      }
    }, listColumnName);

    listFieldHaveMultipleValue = uniq(listFieldHaveMultipleValue);

    result.errors = String.format(ERROR_MESSAGE.MULTIPLE_VALUE, listFieldHaveMultipleValue.join(', '));
    return result;
  }

  filterData = head(filterData);
  const listFieldDontHaveValue = [];

  filters = map(filterItem => {
    const value = get(filterItem.columnName, filterData);
    const originalValue = get(COLUMN_NAME_PREFIX.ORIGINAL_VALUE + filterItem.columnName, filterData);
    if (isUndefined(value)) {
      result.success = false;
      listFieldDontHaveValue.push(filterItem.mappingField.fieldNameAlias);
    }
    return flow(
      assign(placeholder, {
        value: value,
        originalValue: originalValue
      }),
      unset('columnName')
    )(filterItem);
  }, filters);

  if (!result.success) {
    // dont find field in current report part, need to reconfig sub report seeting
    result.errors = String.format(ERROR_MESSAGE.CANT_RESOLVE_FIELD, listFieldDontHaveValue.join(', '));
    return result;
  }

  return flow(
    set('fieldMapping', filters),
    omit(['errors'])
  )(result);
}

/**
 * Build filter object for embeded sub report part and sub report popup
 * @param {Object} fieldMapping
 * @param {Object} reportPart
 * @param {Object} subReport
 * @param {Object} overridingFilterValue
 */
function buildFilterDataForEmbededReportPart(fieldMapping, reportPart, subReport, overridingFilterValue) {
  const result = {
    filters: map(filterItem => {
      const key = filterItem.mappingField.fieldNameInSubReport;

      return flow(
        set('key', key),
        set('linkReportFilter', true),
        omit(['mappingField', 'mappingFieldIndex'])
      )(filterItem);
    }, fieldMapping)
  };

  return subReport.reportFilter
    ? assign(result, {
        masterReportId: reportPart.reportId,
        inheritMasterReportFilter: true,
        overridingFilterValue: overridingFilterValue
      })
    : result;
}

/**
 * Build filter object for link type sub report, this fillter object will be convert into URL
 * @param {Object} fieldMapping
 * @param {Object} reportPart
 * @param {Object} fieldElement
 * @param {Object} subReport
 * @param {Object} overridingFilterValue
 */
function buildFilterDataForURL(fieldMapping, reportPart, fieldElement, subReport, overridingFilterValue) {
  const elementInfo = find(info => info.element === fieldElement, reportPart.elementInfo);
  const { container: elementContainer, position: elementPosition } = elementInfo;
  const { reportId: masterReportId, id: masterReportPartId } = reportPart;

  if (!elementInfo || !String.isGuidFormat(masterReportPartId)) {
    return {
      success: false,
      errors: ERROR_MESSAGE.NEED_UPDATE_RESULT
    };
  }

  const fieldsMapping = map(
    filterItem =>
      flow(
        assign(placeholder, {
          key: String.format(
            FILTER_URL.SUB_REPORT_FIELD_MAPPING_KEY.FORMAT,
            elementPosition,
            filterItem.mappingFieldIndex
          )
        }),
        omit(['mappingField', 'mappingFieldIndex']),
        omitBy(isUndefined)
      )(filterItem),
    fieldMapping
  );

  let result = {
    success: true,
    fieldsMapping: isEmpty(fieldsMapping) ? undefined : fieldsMapping,
    overridingFilterValue:
      isEmpty(overridingFilterValue) || !subReport.reportFilter ? undefined : overridingFilterValue,
    inheritMasterReportFilter: !!subReport.reportFilter || undefined
  };

  if (result.fieldsMapping) {
    result = assign(result, {
      masterReportId: masterReportId,
      containerName: elementContainer,
      masterReportPartId: masterReportPartId
    });
  }

  if (result.fieldsMapping || result.overridingFilterValue || result.inheritMasterReportFilter) {
    result = assign(result, {
      masterReportId: masterReportId
    });
  }

  return result;
}

/**
 * Load master report model to convert from URL fillter to field mapping in master report part
 * @param {Object} subReportFilterFromQuery
 */
function resolveSubReportFilter(subReportFilterFromQuery) {
  const masterReportId = get('masterReportId', subReportFilterFromQuery);
  const fieldsMapping = get('fieldsMapping', subReportFilterFromQuery);
  const masterReportPartId = get('masterReportPartId', subReportFilterFromQuery);
  const containerName = get('containerName', subReportFilterFromQuery);

  if (masterReportId && masterReportPartId && containerName && !isEmpty(fieldsMapping)) {
    return fetcher
      .loadForEdit(masterReportId)
      .then(data => (!isNil(data.report) ? data.report : data))
      .then(report => {
        const regex = /fm(\d+)_(\d+)/;
        const key = get([0, 'key'], fieldsMapping);
        const elementPosition = toNumber(regex.exec(key)[1]);

        const resolveFieldMapping = flow(
          get('reportPart'),
          find({ id: masterReportPartId }),
          getOr([], ['reportPartContent', containerName, 'elements']),
          find({ position: elementPosition }),
          get('properties.drillDown.subReport.mappingFields'),
          mappingFields => {
            if (isNil(mappingFields)) return [];

            return map(mapping => {
              const mappingKey = mapping.key;
              var mappingIndex = toNumber(regex.exec(mappingKey)[2]);
              return flow(
                omit(['key']),
                assign({
                  key: mappingFields[mappingIndex].fieldNameInSubReport,
                  linkReportFilter: true
                })
              )(mapping);
            }, fieldsMapping);
          }
        )(report);

        let filters = compact(concat(subReportFilterFromQuery.filters, resolveFieldMapping));
        filters = isEmpty(filters) ? undefined : filters;

        return flow(
          omit(['fieldsMapping', 'masterReportPartId', 'containerName']),
          set('filters', filters),
          omitBy(isUndefined)
        )(subReportFilterFromQuery);
      });
  }

  return Promise.resolve(subReportFilterFromQuery);
}

export default {
  buildFilterDataCommon,
  buildFilterDataForEmbededReportPart,
  buildFilterDataForURL,
  resolveSubReportFilter
};



// WEBPACK FOOTER //
// ./ui/share/utils/Izenda.SubReportUtils.js